<template lang="pug">
div
  h1.unicorn-h1.mt-4 Создание заявки

  .flex.flex-col.gap-4.my-8(class="md:flex-row md:items-center md:justify-between")
    SelectClasses(
      @class-selected="onClassSelected"
      :selectedClassId="$route.params.classId"
      :classes="classes"
    )
    
    .flex.items-center.justify-between.gap-4(v-if="$route.params.classId" class="md:justify-end")
      .flex.items-center.gap-2(v-if="learners.length > 0")
        u-switch.master-toggle(
          @change="toggleAllCouponsImmediate"
          :modelValue="allCouponsSelected"
          :variant="allCouponsSelected ? 'success' : 'primary'"
          :icon="allCouponsSelected ? 'mdi-checkbox-multiple-marked' : 'mdi-checkbox-multiple-blank-outline'"
          :label="allCouponsSelected ? 'Снять всё' : 'Выбрать всё'"        )
      
      .flex.gap-2
        ArchiveButton
        RecoveryButton

  .grid.grid-cols-1(class="md:mb-0")
    Request(
      v-if="$route.params.classId"
      v-show="ok && !loading"
      ref="requestComponent"
      :key="$route.params.classId"
      :learners="learners"
      :classInfo="classInfo"
      @close-request="closeRequestConfirm"
    )

    CoreLoading(v-if="loading")

    .px-4.my-4(v-if="!loading && !ok && $route.params.classId")
      u-alert(variant="danger" icon="mdi-alert")
        template(#title) Данные класса не загружены
        p {{ loadErrorText }}
        u-button.mt-4(@click="reload" variant="secondary" text) Повторить

    u-alert(v-if="!loading && !$route.params.classId" variant="info" icon="mdi-information")
      template(#title) Как оформить заявку?
      | Для оформления заявки выберите соответсвующий класс из списка доступных выше.

    u-dialog(persistent width="360" v-model="confirm")
      template
        u-card
          u-card-title Отправка заявки
          u-card-body
            u-alert(variant="danger") Внимание! Это действие нельзя отменить после подтверждения
            p.mt-4.font-medium Вы отправляете заявку в столовую:
            table.mt-2
              tr(v-for="entry in waitingRequest.stats" :key="entry.id")
                td {{ entry.name }}
                td: span.ml-2.text-red-600 {{ entry.count }} шт.
          u-card-actions
            u-spacer
            u-button.mr-2(@click="confirm = false" variant="secondary" text :disabled="submitting") Отмена
            u-button(@click="closeRequest" variant="danger" :disabled="submitting") Продолжить

    u-dialog(width="420" v-model="submitError.visible")
      template
        u-card
          u-card-title Заявка не отправлена
          u-card-body
            p {{ submitError.text }}
            p.mt-2.text-xs.text-gray-500(v-if="submitError.ambiguous")
              | Повторная отправка создаст вторую заявку, если первая всё-таки оформилась.
          u-card-actions
            u-spacer
            u-button.mr-2(@click="checkRequestState" variant="secondary" text :disabled="submitting") Проверить состояние
            u-button(v-if="submitError.retryable" @click="resendRequest" variant="danger" :disabled="submitting") Отправить повторно
</template>

<script>
import { mapState } from "vuex";
import { sortBy } from "lodash";

import Api from "../services/Api";
import CoreLogo from "@/app/shared/core/CoreLogo";
import UserProfile from "@/app/shared/new-component/UserProfile";
import AppLogo from "@/app/shared/new-component/AppLogo";
import AppSidebar from "@/app/shared/new-component/AppSidebar";

const DOMAIN_ERROR_TEXTS = {
  402: "Недостаточно средств. Попробуйте обновить страницу (клавиша F5)",
  404: "Заявка не найдена. Попробуйте перезагрузить страницу (клавиша F5)",
  409: "Повторное оформление заявки",
  422: "Ошибка в 'Своем заказе'"
};

const CLOUD_ERROR_TEXTS = {
  500: "Ошибка сервера. Попробуйте позднее",
  502: "Тех. работы на сервере. Попробуйте через 5 минут"
};

export default {
  components: { AppSidebar, AppLogo, UserProfile, CoreLogo },
  data() {
    return {
      learners: [],
      classInfo: null,
      classes: [],
      loadedClassId: null,

      confirm: false,

      waitingRequest: { classId: null, body: null, stats: [] },

      submitError: {
        visible: false,
        text: "",
        ambiguous: false,
        retryable: false
      },

      ok: false,
      loading: false,
      submitting: false,
      error: null,

      allCouponsSelected: false
    };
  },

  computed: {
    ...mapState("connection", ["reloadToken"]),

    loadErrorText() {
      if (!this.error) return "Не удалось загрузить данные класса.";

      const { kind, status } = Api.describeError(this.error);

      if (kind === "session") return "Сессия недействительна, войдите заново.";

      return (
        DOMAIN_ERROR_TEXTS[status] ||
        "Нет связи со школьным сервером. Данные класса появятся, как только связь восстановится."
      );
    }
  },

  watch: {
    async $route() {
      const { classId } = this.$route.params;
      if (!classId) return;

      await this.load(classId);
    },

    async reloadToken() {
      await this.load(this.$route.params.classId);
    }
  },

  async mounted() {
    await this.load(this.$route.params.classId);
  },

  methods: {
    /**
     * Данные уже показанного класса при сбое не стираем: их заменит следующий
     * успешный ответ, а причину покажет полоса связи.
     */
    async load(classId) {
      // Смена класса: данные предыдущего класса, его незавершённая заявка и
      // выбранные позиции не должны показываться и уходить в отправку.
      if (classId !== this.loadedClassId) {
        this.loadedClassId = classId;
        this.learners = [];
        this.classInfo = null;
        this.ok = false;
        this.error = null;
        this.confirm = false;
        this.waitingRequest = { classId: null, body: null, stats: [] };
        this.submitError = { ...this.submitError, visible: false };
        this.allCouponsSelected = false;
      }

      this.loading = !this.learners.length;

      try {
        await this.loadClasses();

        if (classId) {
          await this.loadClassData(classId);
        }

        this.ok = true;
        this.error = null;
      } catch (error) {
        this.error = error;
        if (!this.learners.length) this.ok = false;
      } finally {
        this.loading = false;
      }
    },

    reload() {
      this.$store.dispatch("connection/bumpReload");
    },

    async loadClasses() {
      try {
        const { data: classes } = await Api.classes.getClasses();
        this.classes = classes;
      } catch (error) {
        console.error("Error loading classes:", error);
        throw error;
      }
    },

    onClassSelected(classItem) {
      this.classInfo = classItem;
    },

    async loadClassData(classId) {
      await this.loadEntrants(classId);

      this.classInfo = this.classes.find(c => c.id == classId);

      if (!this.classInfo) {
        await this.loadClasses();
        this.classInfo = this.classes.find(c => c.id == classId);
      }
    },

    async loadEntrants(classId) {
      const { data: learners } = await Api.classes.getClassLearners(classId);
      this.learners = sortBy(learners, ["surname", "name"]);
    },

    toggleAllCouponsImmediate() {
      this.allCouponsSelected = !this.allCouponsSelected;

      requestAnimationFrame(() => {
        if (this.$refs.requestComponent) {
          this.$refs.requestComponent.toggleAllCoupons(this.allCouponsSelected);
        }
      });
    },

    async closeRequestConfirm(req) {
      this.confirm = true;
      this.waitingRequest = req;
    },

    async closeRequest() {
      const { classId, body } = this.waitingRequest;

      if (!classId) {
        this.confirm = false;
        return;
      }

      // Повторный вход, пока запрос в полёте, отправил бы вторую заявку.
      if (this.submitting) return;

      this.submitting = true;
      this.submitError = { ...this.submitError, visible: false };

      let sent = false;

      try {
        await Api.requests.closeRequest(classId, body);
        sent = true;
      } catch (error) {
        this.submitError = this.describeSubmitError(error);
      } finally {
        this.submitting = false;
        this.confirm = false;
      }

      if (!sent) return;

      // Заявка ушла — только теперь сбрасываем выбор, чтобы «Отмена» в
      // подтверждении не стирала расстановку.
      this.allCouponsSelected = false;
      this.ok = true;
      this.error = null;
      this.waitingRequest = { classId: null, body: null, stats: [] };

      if (this.$refs.requestComponent) {
        this.$refs.requestComponent.resetAllSelections();
      }

      await this.refreshAfterSubmit(classId);
    },

    /**
     * Перечитывание данных после успешной отправки — отдельно от самой отправки.
     * Заявка уже создана, поэтому сбой загрузки не должен выглядеть как «заявка
     * не отправлена»: иначе пользователь отправит её повторно.
     */
    async refreshAfterSubmit(classId) {
      try {
        await this.loadClassData(classId);
      } catch (error) {
        this.error = error;
        if (!this.learners.length) this.ok = false;
      }
    },

    /**
     * Автоматического повтора отправки нет: школа создаёт новую заявку на каждый
     * вызов, поэтому неоднозначный исход пользователь разбирает сам.
     */
    describeSubmitError(error) {
      const { kind, reason, status } = Api.describeError(error);

      if (kind === "school_down") {
        const notSent = reason === "no_ping";

        return {
          visible: true,
          ambiguous: !notSent,
          retryable: true,
          text: notSent
            ? "Нет связи со школьным сервером — заявка не отправлена. Повторите отправку, когда связь восстановится."
            : "Школьный сервер не ответил. Заявка могла быть создана — проверьте состояние класса перед повторной отправкой."
        };
      }

      // Устройство не в сети — запрос точно не ушёл.
      if (kind === "offline") {
        return {
          visible: true,
          ambiguous: false,
          retryable: true,
          text:
            "Нет соединения с сетью — заявка не отправлена. Повторите отправку, когда связь восстановится."
        };
      }

      // Таймаут или 5xx: ответа нет, и утверждать «заявка не отправлена» нельзя —
      // она могла создаться. Это важно понимать перед повторной отправкой.
      if (kind === "cloud_down") {
        return {
          visible: true,
          ambiguous: true,
          retryable: true,
          text:
            CLOUD_ERROR_TEXTS[status] ||
            "Сервис не ответил на запрос. Заявка могла быть создана — проверьте состояние класса перед повторной отправкой."
        };
      }

      // Доменный отказ школы: у little в теле ответа есть осмысленное message
      // (например «имеет социальные категории и не может приобретать комплексы»),
      // поэтому показываем его, а статусные тексты оставляем фолбэком.
      const payload = error && error.response && error.response.data;
      const serverMessage =
        payload && typeof payload.message === "string"
          ? payload.message.trim()
          : "";

      return {
        visible: true,
        ambiguous: false,
        retryable: false,
        text:
          serverMessage ||
          DOMAIN_ERROR_TEXTS[status] ||
          "Не удалось отправить заявку. Попробуйте обновить страницу."
      };
    },

    async checkRequestState() {
      this.submitError = { ...this.submitError, visible: false };
      await this.load(
        this.$route.params.classId || this.waitingRequest.classId
      );
    },

    resendRequest() {
      this.submitError = { ...this.submitError, visible: false };
      this.closeRequest();
    }
  }
};
</script>
<style scoped>
.switches-wrapper {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  min-height: 40px;
}

.mobile-switch,
.desktop-switch {
  isolation: isolate;
  position: relative !important;
  z-index: 0 !important;
  transition: none !important;
}

.mobile-switch >>> *,
.desktop-switch >>> * {
  z-index: 0 !important;
  transition: none !important;
}

.mobile-switch >>> .v-input__icon,
.mobile-switch >>> .mdi,
.mobile-switch >>> i,
.mobile-switch >>> .v-icon,
.mobile-switch >>> svg,
.desktop-switch >>> .v-input__icon,
.desktop-switch >>> .mdi,
.desktop-switch >>> i,
.desktop-switch >>> .v-icon,
.desktop-switch >>> svg {
  z-index: 0 !important;
  position: relative !important;
  transition: none !important;
}

@media (max-width: 767px) {
  .mobile-switch {
    display: inline-flex !important;
    align-items: center !important;
    vertical-align: middle !important;
    flex-shrink: 0;
  }

  .mobile-switch >>> .v-input,
  .mobile-switch >>> .v-input__control,
  .mobile-switch >>> .v-input__slot {
    min-height: 40px !important;
    transition: none !important;
  }
}
</style>
